import type { CategoryPrompt } from "../types";

import React from "react";

import {
  AlarmClock,
  ClipboardList,
  FileText,
  Gift,
  Images,
  Megaphone,
  MessageCircle,
  Package,
  Repeat,
  Search,
  ShoppingBag,
  Timer,
} from "lucide-react";

export const accessoriesPrompts: CategoryPrompt[] = [
  {
    icon: <ClipboardList />,
    title: "Run Quiz Funnels with First-Order Discounts",
    description:
      "Drive paid traffic to an interactive quiz that captures emails, then show personalized product recommendations with a first-order discount to convert browsers into buyers.",
    prompt: `Build a quiz funnel landing page for my accessories brand.

The page should include:
- An engaging quiz with 4-6 questions about style preferences and occasions
- Email capture gate before showing results
- Personalized product recommendations based on quiz answers
- First-order discount offer (e.g., 10-15% off) displayed prominently
- Clear CTAs to shop recommended products`,
  },
  {
    icon: <FileText />,
    title: "Create Listicle Advertorials that Sell",
    description:
      "Build a listicle advertorial that solves a specific problem and naturally positions your product as the solution.",
    prompt: `Build a listicle advertorial that solves a specific problem and naturally positions your product as the solution.

**TOPIC:** e.g., "7 Signs You're Wasting Money on Bad Coffee" OR "5 Skincare Mistakes Making You Look Older"

**PRODUCT SOLUTION:** e.g., "Our premium grinder solves mistake #3" OR "Our retinol serum fixes sign #4"

**Reader Type:** Choose one:
- Problem-aware (knows issue, needs solution)
- Researching solutions
- Unaware they have this problem

**Tone & Style:**
- Educational expert, not salesy
- Conversational and relatable
- Build trust before pitching
- Product mentions feel like genuine recommendations

**CTA Guidelines:** Each CTA should:
- Escalate in directness (soft → mid → strong)
- Match the item's context naturally
- Include social proof or urgency where relevant
- Use action language ("See the solution" not "Learn more")

**Conversion Tactics Use:**
- Before/after scenarios in list items
- Comparison language ("Unlike other options...")
- Customer testimonials as mini proof points
- Limited-time offers on specific products
- Risk reversal mentions ("60-day guarantee")

**Key Elements to Include:**
- **Hook:** Relatable frustration or surprising stat (no product yet)
- **List Items (5-10):** Numbered problem → explanation → your product as solution
- **Product Callouts:** 1-2 per item, feels organic not forced
- **Social Proof:** "12,000+ customers fixed this with..."
- **Comparison Boxes:** Your product vs alternatives (1-2 throughout)
- **CTAs:** Soft after item #2, mid after item #5, strong at end (4-7 total)
- **Sidebar:** Persistent product showcase with reviews
- **Conclusion:** Summary + final conversion push

**Goal:** Convert educational content readers into buyers by positioning your product as the obvious fix to their problem.`,
  },
  {
    icon: <Search />,
    title: "Build Occasion-Based Product Finders",
    description:
      "Create a quiz asking about use case, then show 3-5 curated product matches with explanations, gated behind email capture.",
    prompt: `Build an occasion-based product finder quiz that captures emails and matches customers with curated product recommendations.

**OCCASION/USE CASE:** e.g., "Find Your Perfect Gift" OR "Which Coffee Setup Is Right for You?" OR "Your Ideal Skincare Routine"

**PRODUCT POOL:** e.g., 8-12 products that can be matched based on answers

**Quiz Taker Type:** Choose one:
- Gift buyers (shopping for others)
- Self-purchase (solving own problem)
- Indecisive browsers (need guidance)

**Tone & Style:**
- Friendly, helpful guide
- Conversational questions (not formal survey)
- Excitement-building ("Let's find your match!")
- Personalized results language

**Quiz Question Guidelines:** Each question should:
- Be simple and fast (3-5 answer options max)
- Use visual answer choices when possible
- Build toward specific product attributes
- Feel relevant, not random
- 4-6 total questions maximum

**Email Gate Strategy:**
- Place after question #3 or #4 (not immediately)
- Frame as "unlock your personalized results"
- Offer incentive: "Get matches + 15% off code"
- Single field: email only (no name required)
- Trust badge: "We respect your inbox"

**Results Page Elements:**
- **Personalized Headline:** "Based on your answers, here's what we recommend for [their occasion]"
- **Top Match (Hero):** #1 product with "Perfect for you because..." explanation
- **2-4 Additional Matches:** Ranked alternatives with brief why-this-works
- **Product Cards Include:** Image, price, key benefit, star rating, quick CTA
- **Match Reasoning:** "You said [answer], so we picked [product] because..."
- **CTAs on Each Product:** "Shop Your Match" or "Add to Cart"
- **Retake Option:** "Not quite right? Retake quiz"
- **Email Confirmation:** "Check your inbox for your matches + discount code"

**Conversion Tactics Use:**
- Scarcity on discount code ("Code expires in 24 hours")
- Bundle suggestion ("Customers also bought these together")
- Social proof per product ("4.8★ from 2,000+ reviews")
- Limited stock indicators if applicable
- Free shipping threshold callout

**Key Quiz Questions to Include:**
- Occasion/purpose (gifts, daily use, special event)
- Recipient or user type (for them, beginners, experts)
- Budget range (under $50, $50-100, luxury)
- Preferences (scent, style, features, priorities)
- Deal-breakers or must-haves

**Goal:** Capture qualified leads through value exchange while guiding overwhelmed shoppers to products they'll actually buy.`,
  },
  {
    icon: <Images />,
    title: "Turn Social Polls into Shoppable Galleries",
    description:
      "Run Instagram or TikTok polls linking to a shoppable gallery filtered by their answers with 'Tap to Shop' overlays.",
    prompt: `Create a shoppable gallery landing page for my accessories brand.

Include:
- Gallery of products organized by poll results/categories
- Filter options based on common poll answers
- "Tap to Shop" overlays on product images
- Quick-view functionality
- Social proof (likes, saves) displayed on products
- Instagram/TikTok style grid layout`,
  },
  {
    icon: <Timer />,
    title: "Launch Limited Edition Drops with Countdown Timers",
    description:
      "Create a drop page with countdown timer and inventory counter, using scarcity messaging and exit-intent popups.",
    prompt: `Build a limited edition product drop page with countdown timers and real-time scarcity to drive immediate purchases.

**DROP PRODUCT:** e.g., "Limited Edition Summer Bundle" OR "Exclusive Collab Collection" OR "Anniversary Box"

**SCARCITY MECHANISM:** Choose one:
- Time-limited (48-hour flash drop)
- Quantity-limited (500 units only)
- Both (Ends Monday OR when sold out)

**Buyer Type:** Choose one:
- FOMO-driven impulse buyers
- Collectors/exclusivity seekers
- Loyal customers waiting for drops

**Tone & Style:**
- High-energy and urgent
- Exclusive club language ("Only for our community")
- Hype-building and aspirational
- Direct, action-focused copy

**Timer & Counter Guidelines:**
- **Countdown Timer:** Large, above-fold, always visible (hours:minutes:seconds)
- **Inventory Counter:** Live units remaining ("Only 47 left in stock!")
- **Update Frequency:** Real-time or every 30 seconds
- **Visual Treatment:** Bold, contrasting colors, unmissable
- **Mobile Sticky:** Timer follows scroll on mobile

**CTA Guidelines:** Each CTA should:
- Use urgent action words ("Claim Yours Now" not "Shop")
- Reference scarcity ("Secure Your Limited Edition")
- Be oversized and high-contrast
- Repeat every 1-2 scroll sections
- Include inventory status nearby

**Conversion Tactics Use:**
- Multiple countdown timers (header, mid-page, footer)
- Live purchase notifications ("Sarah in NYC just bought this")
- Waitlist for sold-out option
- "Almost gone" alerts at 20% inventory
- Exit-intent popup with final offer
- Urgency stacking ("2 hours left + only 23 remaining")

**Key Elements to Include:**
- **Hero Section:** Product image/video, countdown timer, inventory counter, primary CTA
- **Exclusivity Statement:** "Never available again" or "Limited to 500 units worldwide"
- **What Makes It Special:** Unique features, packaging, or collab story (3-4 bullets)
- **Social Proof Bar:** "1,200+ people viewing this now" or recent purchases
- **Comparison:** Limited edition vs. regular (what's exclusive)
- **Purchase Counter:** "347 claimed in last 24 hours"
- **FAQ:** 2-3 quick answers (Shipping? Returns? Restock?)
- **CTAs:** Hero CTA, mid-page CTA, sticky footer CTA, exit popup (4-5 total)
- **Email Capture (if sold out):** "Join waitlist for next drop"

**Exit-Intent Popup:**
- Trigger: Mouse moves to leave page
- Headline: "Wait! Only [X] left and [time] remaining"
- Offer: Extra incentive if applicable ("Free express shipping on drop orders")
- CTA: "Claim Before It's Gone"
- One-time show per session

**Mobile Optimization:**
- Sticky countdown timer bar at top
- Sticky "Buy Now" button at bottom
- Quick-tap add to cart (minimize friction)
- Large, thumb-friendly CTAs

**Goal:** Create maximum urgency through time + quantity scarcity to drive immediate conversions before drop ends or sells out.`,
  },
  {
    icon: <AlarmClock />,
    title: "Build Early Access Waitlists",
    description:
      "Build a page to collect emails for early access to a new product.",
    prompt: `Build an early access waitlist page that generates excitement and captures high-intent emails before product launch.

**PRODUCT LAUNCH:** e.g., "New Ultra-Hydrating Night Cream" OR "Premium Coffee Subscription Box" OR "Limited Sneaker Collection"

**LAUNCH TIMING:** e.g., "Drops in 3 weeks" OR "Coming Spring 2026" OR "Launching Soon"

**Waitlist Audience:** Choose one:
- Existing customers (insider access)
- Cold audience (building hype)
- Interested prospects (teased via social/ads)

**Tone & Style:**
- Exclusive and aspirational
- Teasing but not revealing everything
- Insider language ("Be the first to know")
- Anticipation-building without being salesy

**Email Capture Guidelines:**
- **Field Requirements:** Email only (name optional)
- **Placement:** Above-fold, center focus
- **CTA Copy:** "Join the Waitlist" or "Get Early Access"
- **Incentive:** "Waitlist members get 20% off at launch"
- **Confirmation:** Instant success message with what's next

**CTA Guidelines:** Each CTA should:
- Emphasize exclusivity ("Reserve Your Spot")
- Reference the benefit ("Get Launch Day Access")
- Be large and singular (one primary action)
- Include social proof nearby ("2,847 joined")

**Conversion Tactics Use:**
- Launch countdown timer (days until drop)
- Live waitlist counter ("3,421 people waiting")
- Tiered perks (first 500 get extra bonus)
- Referral incentive ("Share & move up the list")
- Sneak peek reveals (blurred product images)
- FOMO messaging ("Limited quantities at launch")

**Key Elements to Include:**
- **Hero Section:** Product teaser image/video (partial reveal), headline hook, email capture form
- **Why Join:** 3-4 benefit bullets ("Early access," "Launch discount," "Limited availability")
- **Sneak Peek:** Blurred or partial product shots with intrigue copy
- **Waitlist Counter:** "Join [X] others waiting" (builds social proof)
- **Launch Timeline:** "Expected drop: [date]" or countdown
- **Exclusive Perks:** What waitlist members get vs. general public
- **Social Proof:** Testimonials from beta testers or previous launches
- **Referral Mechanic:** "Share your link, move up 5 spots per referral"
- **FAQ:** 2-3 questions (When launches? Cost? How notified?)
- **Secondary CTA:** Bottom of page reminder to join

**Post-Signup Experience:**
- **Confirmation Page:** "You're on the list! Check email for details"
- **Waitlist Position:** "You're #2,847 in line"
- **Share Prompt:** Social sharing buttons with pre-written copy
- **Next Steps:** "We'll email you 24 hours before launch"

**Mobile Optimization:**
- Email form stays above-fold
- Single-tap social sharing
- Quick-load teaser visuals
- Minimal scrolling required

**Goal:** Build qualified email list of high-intent buyers who will convert immediately at launch through exclusivity and early access incentives.`,
  },
  {
    icon: <Package />,
    title: "Build Interactive Bundle Builders",
    description:
      "Create a tool letting customers select a base product and add complementary items with progressive discounts and visual previews.",
    prompt: `Build an interactive bundle builder page for my accessories brand.

Include:
- Step-by-step bundle creation flow
- Base product selection as first step
- Complementary product suggestions for each step
- Progressive discount display (save 10% on 2, 15% on 3, etc.)
- Visual preview of the complete bundle
- Running total showing original vs. bundle price
- "Complete Your Bundle" CTA`,
  },
  {
    icon: <ShoppingBag />,
    title: "Drive Free Shipping Threshold Conversions",
    description:
      "Build a page with progress bar showing proximity to free shipping and smart product recommendations to hit minimum spend.",
    prompt: `Build a cart or product page with dynamic free shipping progress bar and strategic upsells to push customers over the threshold.

**SHIPPING THRESHOLD:** e.g., "$50 for free shipping" OR "$75 for free express shipping"

**CURRENT CART VALUE:** e.g., "$32" OR "$58" OR "$12"

**Customer Stage:** Choose one:
- Cart page (reviewing items)
- Product page (first add)
- Checkout intercept (leaving without hitting threshold)

**Tone & Style:**
- Encouraging and helpful
- Value-focused (saving money motivation)
- Celebratory when threshold hit
- No pressure, just opportunity

**Progress Bar Guidelines:**
- **Placement:** Top of page, sticky on scroll
- **Visual:** Animated fill bar showing progress to goal
- **Copy Format:** "Add $18 more for FREE SHIPPING!" or "You're $18 away!"
- **Success State:** "🎉 You unlocked FREE SHIPPING!"
- **Color Change:** Red/yellow (far) → green (achieved)
- **Always Visible:** Sticky header or floating banner

**CTA Guidelines:** Each product CTA should:
- Show exact addition impact ("Add this = Free Shipping!")
- Use additive language ("Complete Your Order")
- Include quick-add functionality (no leaving page)
- Display original price + threshold math

**Conversion Tactics Use:**
- Exact dollar gap messaging ("Just $12.50 more!")
- Perfect-fit product suggestions (items that exactly hit threshold)
- Bundle recommendations that exceed by small amount
- Countdown urgency ("Offer ends today")
- Comparison savings ("$8 shipping vs. $12 product")
- Quick-add buttons (no cart navigation needed)

**Key Elements to Include:**
- **Progress Bar (Sticky):** Current amount, goal amount, gap remaining
- **Recommendation Headline:** "Add one of these to unlock free shipping:"
- **Product Grid:** 4-6 items strategically priced to hit/exceed threshold
- **Price Highlighting:** Show how each item affects total ("$32 → $47")
- **Quick-Add CTAs:** One-click add without leaving page
- **Bestseller Badges:** "Most added to hit free shipping"
- **Category Filters:** "Under $20" or "Small Add-Ons" for easy browsing
- **Cart Summary (Sidebar):** Running total with live threshold progress
- **Success Celebration:** Confetti or checkmark when threshold achieved
- **Secondary Benefit:** "Free shipping + arrives by [date]"

**Product Recommendation Strategy:**
- **Tier 1:** Items that exactly hit threshold (prioritize these)
- **Tier 2:** Items $2-5 over threshold (slight overage)
- **Tier 3:** Complementary products to cart items
- **Sort Order:** Closest to gap amount first
- **Price Display:** "$15 (Only $3 more needed!)"

**Dynamic Messaging:**
- **$25+ away:** "You're close! Add $X for free shipping"
- **$10-25 away:** "Almost there! Just $X more"
- **Under $10 away:** "So close! Only $X to go"
- **Threshold hit:** "Free shipping unlocked! 🎉"

**Mobile Optimization:**
- Sticky progress bar at top
- Horizontal scroll product carousel
- One-tap quick-add buttons
- Simplified cart summary

**Goal:** Increase average order value by motivating customers to add complementary products to reach free shipping threshold instead of paying delivery fees.`,
  },
  {
    icon: <Megaphone />,
    title: "Turn Influencer UGC into Performance Ads",
    description:
      "Gift products to micro-influencers, collect content rights, then turn top-performing UGC into paid ads with creator codes on dedicated pages.",
    prompt: `Build an influencer UGC landing page for my accessories brand.

Include:
- Hero featuring top influencer content
- Gallery of UGC from multiple creators
- Creator-specific discount codes
- "Shop [Creator Name]'s Picks" sections
- Video content integration
- Social proof metrics (views, engagement)
- Easy shop functionality on each UGC piece`,
  },
  {
    icon: <MessageCircle />,
    title: "Create Customer Story Advertorials",
    description:
      "Build advertorials featuring customer transformation stories with CTAs throughout.",
    prompt: `Build a customer story advertorial that showcases a real transformation and positions your product as the key solution.

**CUSTOMER STORY:** e.g., "How Sarah Finally Got Rid of Adult Acne After 10 Years" OR "This Dad Lost 30 Pounds Using One Simple Morning Routine"

**PRODUCT FEATURED:** e.g., "Our acne treatment system" OR "Our protein coffee blend"

**Story Type:** Choose one:
- Problem-to-solution transformation
- Before/after physical change
- Lifestyle improvement journey

**Tone & Style:**
- Authentic and relatable (real person voice)
- Vulnerable about struggles
- Hopeful and inspiring
- Natural product mentions (not forced)

**CTA Guidelines:** Each CTA should:
- Reference the customer's success ("Get Sarah's routine")
- Use first-person language from story ("This changed everything for me")
- Escalate throughout article (learn more → try it → buy now)
- Include trust elements (reviews, guarantee)

**Conversion Tactics Use:**
- Before/after photos (authentic, not stock)
- Specific metrics ("Lost 30 lbs in 4 months")
- Failed alternatives mentioned ("I tried everything...")
- Emotional turning points ("The moment I discovered...")
- Product as hero reveal (midway through story)
- Limited-time offer for readers ("Sarah's discount code")

**Key Elements to Include:**
- **Headline:** Problem + timeframe + outcome ("How I Cleared My Skin in 60 Days")
- **Hero Image:** Before/after or customer photo
- **Opening Hook:** Relatable struggle (2-3 paragraphs, no product yet)
- **The Breaking Point:** Why they needed change NOW
- **Discovery Story:** How they found your product (feels organic)
- **Experience Details:** What using product was like, specific routine
- **Results Timeline:** Week 1, Week 4, Week 8 progress updates
- **Before/After Comparison:** Visual proof or detailed description
- **Product Callout Boxes:** 2-3 highlighted mentions with features/benefits
- **Quote Pulls:** Emotional statements as visual breaks
- **Other Customer Proof:** "Sarah isn't alone - 4,500+ customers saw results"
- **CTAs:** Soft after problem setup, mid after discovery, strong after results (4-6 total)
- **FAQ from Customer POV:** "What Sarah wishes she knew before starting"
- **Final Testimonial:** Closing quote about life after transformation

**Story Structure Flow:**
- **Act 1:** The struggle (build empathy)
- **Act 2:** The discovery (introduce product naturally)
- **Act 3:** The transformation (show specific results)
- **Closing:** Life now + encouragement to try

**Trust-Building Elements:**
- Real customer name + photo (with permission)
- Specific dates and timelines
- Unfiltered language and honesty
- Challenges mentioned (not just wins)
- Doctor/expert validation if applicable
- Money-back guarantee callouts

**Mobile Optimization:**
- Shorter paragraphs for readability
- Before/after images stacked vertically
- Sticky CTA following scroll
- Quick-loading customer photos

**Goal:** Convert skeptical readers into buyers by showing authentic transformation proof that makes your product's benefits tangible and believable.`,
  },
  {
    icon: <Repeat />,
    title: "Build Subscription Landing Pages",
    description:
      "Create a subscription page emphasizing convenience and savings, with customization options and flexibility to skip or cancel anytime.",
    prompt: `Build a subscription landing page that emphasizes convenience, savings, and flexibility to convert one-time buyers into recurring customers.

**SUBSCRIPTION OFFER:** e.g., "Monthly Coffee Delivery" OR "Skincare Refill Club" OR "Snack Box Subscription"

**VALUE PROPOSITION:** e.g., "Save 20% + never run out" OR "Custom boxes delivered automatically"

**Subscription Model:** Choose one:
- Replenishment (same product, timed delivery)
- Curated boxes (surprise selections)
- Build-your-own (custom each month)

**Tone & Style:**
- Convenient and effortless
- Savings-focused without being cheap
- Flexible and low-commitment
- Lifestyle-enhancing language

**CTA Guidelines:** Each CTA should:
- Emphasize savings ("Start Saving 20%")
- Use commitment-free language ("Try Your First Box")
- Reference ease ("Set It & Forget It")
- Include flexibility reminder ("Cancel Anytime")

**Conversion Tactics Use:**
- Savings calculator showing annual value
- Comparison: subscription vs. one-time pricing
- First box incentive ("Get 30% off your first delivery")
- Testimonials from long-term subscribers
- Flexibility badges (skip, pause, cancel icons)
- Risk reversal ("Cancel after first box, no questions")

**Key Elements to Include:**
- **Hero Section:** Product image, headline with savings %, primary subscription CTA
- **Value Proposition:** 3-4 benefits (save money, convenience, never run out, exclusive perks)
- **Pricing Comparison:** Side-by-side one-time vs. subscription savings
- **How It Works:** 3-step process (Choose plan → Customize → Delivered automatically)
- **Customization Options:** Frequency selector (every 2/4/6 weeks), quantity selector, product variants
- **Flexibility Features:** Skip, pause, cancel badges with "Change anytime" copy
- **Savings Calculator:** "You'll save $147/year with our subscription"
- **Subscriber Perks:** Early access, exclusive products, bonus gifts, free shipping
- **Social Proof:** "Join 12,000+ subscribers" + testimonials focused on convenience
- **Delivery Preview:** What's included, packaging, first box experience
- **FAQ:** 4-5 questions (How to cancel? Can I skip? When charged? Shipping?)
- **CTAs:** Hero CTA, after pricing, after how-it-works, footer (4-5 total)
- **Risk Reversal:** "Try risk-free for 30 days" or "Love it or cancel free"

**Pricing Display Strategy:**
- **Monthly Price:** Large, prominent ($24.99/month)
- **Per-Item Breakdown:** "Just $6.25 per product"
- **Savings Badge:** "Save 20% vs. one-time purchase"
- **Annual Savings:** "$147 saved per year"
- **Comparison Strikethrough:** ~~$29.99~~ $24.99

**Flexibility Messaging:**
- **Skip Deliveries:** "Going on vacation? Skip with one click"
- **Pause Anytime:** "Need a break? Pause your subscription"
- **Cancel Free:** "No contracts, cancel anytime online"
- **Change Products:** "Swap products or adjust quantity easily"
- **Visual Icons:** Calendar (skip), pause button, X (cancel)

**First Box Incentive:**
- Special discount (30% off first box)
- Bonus gift with first delivery
- Free upgrade or sample
- Exclusive welcome item

**Trust Elements:**
- No hidden fees disclosure
- Transparent cancellation policy
- Subscriber testimonials on convenience
- Secure payment badges
- "Delivered by [date]" assurance

**Mobile Optimization:**
- Simplified frequency selector
- Swipeable pricing comparison
- Sticky subscription CTA
- One-tap customization options

**Goal:** Convert skeptical shoppers into subscribers by emphasizing massive convenience, tangible savings, and zero-risk flexibility that makes committing feel safe and smart.`,
  },
  {
    icon: <Gift />,
    title: "Launch Gift Card Promotions",
    description:
      "Build a gift card promo page with bonus credit offers (buy $100, get $25 free) and urgency messaging, noting recipients typically overspend.",
    prompt: `Create a gift card promotion landing page for my accessories brand.

Include:
- Hero promoting bonus credit offer (e.g., buy $100, get $25 free)
- Gift card value tiers with bonus amounts
- Urgency messaging (limited time offer)
- Benefits section (recipients typically spend more than card value)
- Easy digital delivery options
- Physical card option if available
- Perfect for any occasion messaging
- Purchase CTA`,
  },
];
